import React, { useMemo, useContext, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import { isUndefined } from 'underscore';
import {
  Wizard,
  LayoutContainer,
  Layout,
  StepContainer,
} from '@bingads-webui-react/wizard';
import {
  SaveBlocker,
  DefaultTemplate,
  CancelWizardDialog,
} from '@bingads-webui-react/wizard-ui-library';
import { FluentSwitcherContext } from '@bingads-webui-react/fluent-version-switcher';
import { withTheme } from 'react-jss';
import config from '@bingads-webui-component-legacy/config/index';
import { getLocalStorage } from '@bingads-webui/storage';
import { withProps } from '@bingads-webui-react/with-props';
import { WizardPerfMarker } from '@bingads-webui-campaign/wizard-performance-marker';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { PerformanceMarkerProvider } from '@bingads-webui-campaign-react/performance-marker-provider';
import { withScope } from '@bingads-webui-react/scope-service';
import { withRecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { withSiteMap, getRoutePathById } from '@bingads-webui-campaign-react/with-site-map';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';

import { StepConfig, FLOWS_TO_FIRST_STEP, FLOWS, getFlow, StepList, PerfMaxStepFooter } from './steps';
import { WizardLogger } from './shared/components/wizard-logger';
import { PMAX_WIZARD_ID_NAME } from './constants';

const getCampaignObjective = (localStorage) => {
  let campaignObjective = config.get('CampaignObjective');
  let providerId = null;
  let salesCountry = null;
  let localInventory = null;
  let isFeedFiltered = null;
  let feedLabel = null;

  if (isUndefined(campaignObjective)) {
    if (localStorage.supportsStorage()) {
      campaignObjective = localStorage.get('CampaignObjective');
      config.set('CampaignObjective', campaignObjective);
    }
  }

  if (localStorage.supportsStorage()) {
    providerId = config.get('ProviderId') || localStorage.get('ProviderId');
    salesCountry = config.get('SalesCountry') || localStorage.get('SalesCountry');
    localInventory = config.get('LocalInventoryAdsEnabled') || localStorage.get('LocalInventoryAdsEnabled');
    isFeedFiltered = config.get('IsFeedFiltered') || localStorage.get('IsFeedFiltered');
    feedLabel = config.get('FeedLabel') || localStorage.get('FeedLabel');
  }

  return {
    campaignObjective,
    providerId,
    salesCountry,
    localInventory,
    isFeedFiltered,
    feedLabel,
  };
};

const PerfMaxNorthStarWizardImpl = ({
  perfMarker,
  scope,
  permissions,
  siteMap,
  dynamicOptions,
}) => {
  const instrumentation = useContext(InstrumentationContext);

  const wizardPerfMarker = useMemo(() => (
    perfMarker.createChild('PerfMaxNorthStarWizard')
  ), [perfMarker]);

  const localStorage = useMemo(() => getLocalStorage(), []);

  const routeToNavigateToWhenDone = useMemo(() => getRoutePathById(siteMap, 'campaigns'), [siteMap]);

  const { dynamic: { AppsAdsPerfMaxFluentV2 } } = permissions;

  const {
    campaignObjective,
    providerId,
    salesCountry,
    localInventory,
    isFeedFiltered,
    feedLabel,
  } = useMemo(() => getCampaignObjective(localStorage), [localStorage]);

  const wizardFlow = useMemo(() => {
    if (providerId !== null && providerId !== '') {
      return FLOWS.RETAIL_PERF_MAX;
    }
    return FLOWS.PERFMAX_WIZARD;
  }, [providerId]);

  const initialStep = FLOWS_TO_FIRST_STEP[wizardFlow];

  const wizardConfig = useMemo(() => ({
    id: PMAX_WIZARD_ID_NAME.PMAX_NORTH_STAR_WIZARD,
    initialStep,
    steps: StepConfig,
    initialFlowFactors: { flow: wizardFlow },
    initialWizardData: {
      flow: wizardFlow,
      campaignObjective,
      providerId,
      salesCountry,
      localInventory,
      isFeedFiltered,
      feedLabel,
      ...dynamicOptions,
    },
    getFlow: ({ flow }) => getFlow({ flow, permissions }),
    actions: {
      onDone: () => scope.save(routeToNavigateToWhenDone),
      onExited: () => scope.save(routeToNavigateToWhenDone),
    },
    attachStepActivities: false,
  }), [initialStep, wizardFlow, campaignObjective, providerId, salesCountry, localInventory, isFeedFiltered, feedLabel, dynamicOptions, permissions, scope, routeToNavigateToWhenDone]);

  useEffect(() => {
    setClarityTag({ PerfMaxNorthStarWizard: FLOWS.PERFMAX_WIZARD });
  }, []);

  return (
    <FluentSwitcherContext.Provider value={AppsAdsPerfMaxFluentV2}>
      <PerformanceMarkerProvider
        name="PerfMaxNorthStarWizard"
        instrumentation={instrumentation}
        immediateDone={false}
      >
        <Scenario name={`PerfMaxNorthStarWizard-${wizardFlow}`}>
          <Activity name="DefaultWizardActivity">
            <Wizard config={wizardConfig}>
              <WizardPerfMarker wizardName="DisplayPlusWizard" wizardMarker={wizardPerfMarker} >
                <LayoutContainer template={DefaultTemplate}>
                  <Layout type="StepContainer" component={StepContainer} />
                  <Layout type="Blocker" component={SaveBlocker} />
                  <Layout type="CancelDialog" component={CancelWizardDialog} />
                  <Layout type="StepList" component={StepList} />
                  <Layout
                    type="Footer"
                    component={PerfMaxStepFooter}
                    nextButtonText={_TL_('Save & go to next step')}
                  />
                </LayoutContainer>
                <WizardLogger />
              </WizardPerfMarker>
            </Wizard>
          </Activity>
        </Scenario>
      </PerformanceMarkerProvider>
    </FluentSwitcherContext.Provider>
  );
};

PerfMaxNorthStarWizardImpl.propTypes = {
  perfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    save: PropTypes.func.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      AppsAdsPerfMaxFluentV2: PropTypes.bool,
    }),
  }).isRequired,
  siteMap: PropTypes.shape({}).isRequired,
  dynamicOptions: PropTypes.shape({}).isRequired,
};

const PerfMaxNorthStarWizardInternal = ({
  adExtensionsDynamicFeaturesLoader,
  permissions,
  theme,
  scope,
  instrumentation,
  i18n,
  ...rest
}) => {
  const dynamicOptionsLoader = useCallback(
    () => Promise.props({
      adExtensionsDynamicFeatures: adExtensionsDynamicFeaturesLoader({
        permissions,
        theme,
        scope,
        instrumentation,
        i18n,
      }),
      adExtensionsAssociationsFactory:
        // eslint-disable-next-line import/no-unresolved
        import(/* webpackChunkName: "perf-max-north-star-campaign-creation" */'@bingads-webui-campaign-react/ad-extensions-associations-factory')
          .then(({ extensionsAssociationsFactory, getObjectiveExtensions }) => ({ extensionsAssociationsFactory, getObjectiveExtensions })),
    })
      .then(({ adExtensionsDynamicFeatures, adExtensionsAssociationsFactory }) => ({
        adExtensionsDynamicFeatures,
        adExtensionsAssociationsFactory,
      }))
      .then(dynamicOptions => ({
        default: withProps({ dynamicOptions }, PerfMaxNorthStarWizardImpl),
      })),
    [adExtensionsDynamicFeaturesLoader, permissions, theme, scope, instrumentation, i18n]
  );

  const LazyView = useMemo(() => React.lazy(dynamicOptionsLoader), [dynamicOptionsLoader]);

  // useEffect(() => {
  //   if (!campaignObjective) {
  //     props.scope.save('/campaign/vnext/createcampaign/objective');
  //   }

  //   return () => {
  //     // eslint-disable-next-line no-console
  //     console.log('[DWD] PerfMaxCampaignCreationPage unmounted');
  //     // clear objective choice in local storage
  //     if (localStorage.supportsStorage()) {
  //       localStorage.invalidate('CampaignObjective');
  //     }
  //   };
  // }, [campaignObjective, props.scope]);

  return (
    <>
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <LazyView
          adExtensionsDynamicFeaturesLoader={adExtensionsDynamicFeaturesLoader}
          permissions={permissions}
          theme={theme}
          scope={scope}
          instrumentation={instrumentation}
          i18n={i18n}
          {...rest}
        />
      </React.Suspense>
      {/* {!campaignObjective &&
        <div data-waiting-redirect="due to no campaign objective" />
      } */}
    </>
  );
};

PerfMaxNorthStarWizardInternal.propTypes = {
  adExtensionsDynamicFeaturesLoader: PropTypes.func.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      AppsAdsPerfMaxFluentV2: PropTypes.bool,
    }),
  }).isRequired,
  theme: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    save: PropTypes.func.isRequired,
  }).isRequired,
  instrumentation: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
};

export const PerfMaxNorthStarWizard = withSiteMap(withPermissions(withRecommendationContext(withScope(withTheme(PerfMaxNorthStarWizardInternal), undefined, undefined, false, true))));
